<template>
  <div class="page-view">

    <BasePageHeader :title="$t('checkoutsOverview.historyTitle')" @back="$router.back()">
      <template #actions>
        <button class="btn-icon" @click="load" :disabled="loading" :title="$t('common.refresh')">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-[22px] h-[22px]">
            <polyline points="23 4 23 10 17 10"/>
            <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
          </svg>
        </button>
      </template>
    </BasePageHeader>

    <!-- Filters -->
    <div class="px-4 py-3 flex flex-col gap-2 border-b border-[var(--color-border)]">
      <!-- Row 1: search + project select -->
      <div class="flex gap-2">
        <div class="flex-1">
          <SearchField v-model="query" :debounce="0" :placeholder="$t('checkoutsOverview.searchPlaceholder')" />
        </div>
        <BaseFormField v-if="uniqueProjects.length" density="compact" class="!mb-0 shrink-0" style="width: 10rem;">
          <BaseSelect v-model="projectFilter" density="compact" :aria-label="$t('checkoutsOverview.allProjects')">
            <option value="">{{ $t('checkoutsOverview.allProjects') }}</option>
            <option v-for="p in uniqueProjects" :key="p" :value="p">{{ p }}</option>
          </BaseSelect>
        </BaseFormField>
      </div>
      <!-- Row 2: status filter chips -->
      <div class="flex items-center gap-2 flex-wrap">
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all shrink-0 border"
          :class="statusFilter === 'all' ? 'bg-red-900 border-red-500 text-red-300' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-100'"
          @click="statusFilter = 'all'; currentPage = 1"
        >{{ $t('common.all') }}</button>
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all shrink-0 border"
          :class="statusFilter === 'active' ? 'bg-red-900 border-red-500 text-red-300' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-100'"
          @click="statusFilter = 'active'; currentPage = 1"
        >{{ $t('common.active') }}</button>
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all shrink-0 border"
          :class="statusFilter === 'returned' ? 'bg-red-900 border-red-500 text-red-300' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-100'"
          @click="statusFilter = 'returned'; currentPage = 1"
        >{{ $t('checkoutsOverview.returned') }}</button>
        <button
          class="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer transition-all shrink-0 border"
          :class="statusFilter === 'overdue' ? 'bg-red-900 border-red-500 text-red-300' : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-100'"
          @click="statusFilter = 'overdue'; currentPage = 1"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="w-[14px] h-[14px]">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {{ $t('common.overdue') }}
        </button>
        <div class="text-xs text-[var(--color-muted)]">
          {{ filtered.length }}<span v-if="overdueCount > 0" class="text-red-400 font-semibold"> &middot; {{ $t('checkoutsOverview.overdueCount', { count: overdueCount }) }}</span>
        </div>
      </div>
    </div>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" :message="loadError" variant="error" />
    <EmptyState v-else-if="!filtered.length" :message="statusFilter === 'overdue' ? $t('checkoutsOverview.noOverdue') : statusFilter === 'returned' ? $t('checkoutsOverview.noReturned') : $t('checkoutsOverview.noCheckoutsFound')" />

    <div v-else class="list-stack">
      <div
        v-for="co in paginatedItems"
        :key="co.id"
        class="data-card cursor-pointer active:scale-[.985]"
        :class="isOverdue(co) ? '!border-red-900 !bg-[#1c0a0a] hover:!border-red-800 hover:!bg-[#270e0e]' : ''"
        @click="navigateToItem(co)"
      >
        <div class="data-card__main">
          <div class="flex items-center gap-2 mb-1">
            <div class="data-card__name">{{ itemName(co) }}</div>
            <span v-if="!isActive(co)" class="badge badge-muted">{{ $t('checkoutsOverview.returned') }}</span>
            <span v-else-if="isOverdue(co)" class="badge badge-danger">{{ $t('common.overdue') }}</span>
            <span v-else class="badge badge-success">{{ $t('common.active') }}</span>
          </div>

          <div v-if="co.item_metadata?.serial_number || co.item_metadata?.asset_tag" class="flex gap-1.5 flex-wrap mb-1.5">
            <span v-if="co.item_metadata?.serial_number" class="badge badge-muted">{{ $t('checkoutsOverview.serialNumber') }} {{ co.item_metadata.serial_number }}</span>
            <span v-if="co.item_metadata?.asset_tag" class="badge badge-muted">{{ $t('checkoutsOverview.assetTag') }} {{ co.item_metadata.asset_tag }}</span>
          </div>

          <div class="data-card__meta">
            <span v-if="co.project" class="badge badge-pill" style="background:#1e3a5f;color:#93c5fd;">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[11px] h-[11px] shrink-0">
                <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
              </svg>
              {{ co.project }}
            </span>
            <span v-if="co.user" class="badge badge-pill badge-muted">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[11px] h-[11px] shrink-0">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
              </svg>
              {{ co.user }}
            </span>
            <span v-else-if="co.anon_user" class="badge badge-pill" style="background:#2d1f4f;color:#c4b5fd;">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[11px] h-[11px] shrink-0">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                <line x1="17" y1="11" x2="23" y2="11"/><line x1="20" y1="8" x2="20" y2="14"/>
              </svg>
              {{ $t('checkout.guest') }}: {{ co.anon_user?.full_name ?? co.anon_user }}
            </span>
          </div>

          <div class="flex flex-wrap gap-3 mt-1">
            <span class="flex flex-col gap-0.5 text-[.8rem] text-gray-300">
              <span class="text-[.7rem] text-gray-500 uppercase tracking-[.04em]">{{ $t('checkoutsOverview.out') }}</span>
              {{ formatDate(co.checkout_date) }}
            </span>
            <span v-if="co.actual_checkin" class="flex flex-col gap-0.5 text-[.8rem] text-gray-300">
              <span class="text-[.7rem] text-gray-500 uppercase tracking-[.04em]">{{ $t('checkoutsOverview.returnedDate') }}</span>
              {{ formatDate(co.actual_checkin) }}
            </span>
            <span v-else-if="co.effective_expected_checkin" class="flex flex-col gap-0.5 text-[.8rem]" :class="isOverdue(co) ? 'text-red-300' : 'text-gray-300'">
              <span class="text-[.7rem] text-gray-500 uppercase tracking-[.04em]">{{ $t('checkoutsOverview.expectedBack') }}</span>
              {{ formatDate(co.effective_expected_checkin) }}
              <span v-if="isOverdue(co)" class="badge badge-danger w-fit">{{ $t('common.overdue') }}</span>
            </span>
            <span v-else class="flex flex-col gap-0.5 text-[.8rem] text-gray-500">
              <span class="text-[.7rem] uppercase tracking-[.04em]">{{ $t('checkoutsOverview.expectedBack') }}</span>
              {{ $t('checkoutsOverview.notSet') }}
            </span>
          </div>
        </div>

        <div class="data-card__actions" @click.stop>
          <button
            v-if="isActive(co)"
            class="btn small primary"
            :disabled="checkingInId === co.id"
            @click="openCheckIn(co)"
          >
            {{ checkingInId === co.id ? $t('checkoutsOverview.checkingIn') : $t('checkoutsOverview.checkIn') }}
          </button>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-[18px] h-[18px] text-gray-600 shrink-0">
            <polyline points="9 18 15 12 9 6"/>
          </svg>
        </div>
      </div>
    </div>

    <div v-if="totalPages > 1" class="flex items-center justify-center gap-3 px-4 py-4">
      <button class="btn ghost small" :disabled="currentPage === 1" @click="currentPage--">← {{ $t('common.prev') }}</button>
      <span class="text-xs text-[var(--color-muted)]">{{ $t('common.pageOf', { page: currentPage, total: totalPages }) }}</span>
      <button class="btn ghost small" :disabled="currentPage === totalPages" @click="currentPage++">{{ $t('common.next') }} →</button>
    </div>

    <BaseConfirmModal
      :show="!!checkInTarget"
      :title="$t('checkoutsOverview.checkInTitle')"
      :body="checkInTarget ? $t('checkoutsOverview.checkInBody', { name: escapeHtml(itemName(checkInTarget)) }) : ''"
      :confirm-label="$t('checkoutsOverview.checkIn')"
      confirm-variant="primary"
      :loading="!!checkingInId"
      @confirm="doCheckIn"
      @cancel="checkInTarget = null"
    />

  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { getAllCheckouts } from '@/api/api.js'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseConfirmModal from '@/components/base/BaseConfirmModal.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import SearchField from '@/components/search/SearchField.vue'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const route  = useRoute()
const { t } = useI18n()

const checkouts    = ref([])
const totalCount   = ref(0)
const overdueCount = ref(0)
const loading      = ref(false)
const loadError    = ref(null)
const checkInTarget = ref(null)
const checkingInId  = ref(null)
const query       = ref('')
// statusFilter: 'all' | 'active' | 'returned' | 'overdue'
const statusFilter  = ref('active')
const projectFilter = ref('')
const pageSize = 25
const currentPage = ref(1)

function itemName(co) {
  return co.item_metadata?.item?.name
    ?? co.item_metadata?.serial_number
    ?? co.item_metadata?.asset_tag
    ?? `#${co.item_metadata?.id ?? co.id}`
}

function isActive(co) { return co.status === 0 || co.status === 2 }

function isOverdue(co) {
  if (!isActive(co)) return false
  if (co.status === 2) return true
  if (!co.effective_expected_checkin) return false
  return new Date(co.effective_expected_checkin) < new Date()
}

watch([query, statusFilter, projectFilter], () => { currentPage.value = 1 })

const uniqueProjects = computed(() => {
  const names = checkouts.value
    .map(co => co.project)
    .filter(p => p && typeof p === 'string')
  return [...new Set(names)].sort()
})

const filtered = computed(() => {
  let list = checkouts.value

  // Status filter
  if (statusFilter.value === 'active')   list = list.filter(isActive)
  else if (statusFilter.value === 'returned') list = list.filter(co => !isActive(co))
  else if (statusFilter.value === 'overdue')  list = list.filter(isOverdue)

  // Project filter
  if (projectFilter.value) {
    list = list.filter(co => co.project === projectFilter.value)
  }

  // Text search
  const q = query.value.trim().toLowerCase()
  if (q) {
    list = list.filter(co => {
      const name    = itemName(co).toLowerCase()
      const project = (co.project ?? '').toLowerCase()
      const user    = (co.user ?? '').toLowerCase()
      const anonName = (co.anon_user?.full_name ?? '').toLowerCase()
      const sn      = (co.item_metadata?.serial_number ?? '').toLowerCase()
      return name.includes(q) || project.includes(q) || user.includes(q) || anonName.includes(q) || sn.includes(q)
    })
  }
  return list
})

// Sort: active+overdue first (overdue before active), returned last; within groups by date
const sorted = computed(() => [...filtered.value].sort((a, b) => {
  // returned items go to the bottom
  const aActive = isActive(a), bActive = isActive(b)
  if (aActive !== bActive) return aActive ? -1 : 1
  // overdue before other active
  const aOverdue = isOverdue(a)
  const bOverdue = isOverdue(b)
  if (aOverdue !== bOverdue) return aOverdue ? -1 : 1
  // within returned: most recently returned first
  if (!aActive && !bActive) {
    const aRet = a.actual_checkin ? new Date(a.actual_checkin) : null
    const bRet = b.actual_checkin ? new Date(b.actual_checkin) : null
    if (aRet && bRet) return bRet - aRet
    return 0
  }
  // within active: expected_checkin ascending (soonest first), nulls last
  const aDate = a.effective_expected_checkin ? new Date(a.effective_expected_checkin) : null
  const bDate = b.effective_expected_checkin ? new Date(b.effective_expected_checkin) : null
  if (!aDate && !bDate) return 0
  if (!aDate) return 1
  if (!bDate) return -1
  return aDate - bDate
}))

const paginatedItems = computed(() => {
  const start = (currentPage.value - 1) * pageSize
  return sorted.value.slice(start, start + pageSize)
})
const totalPages = computed(() => Math.ceil(sorted.value.length / pageSize))

function formatDate(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

function navigateToItem(co) {
  const metaId = co.item_metadata?.id
  if (metaId) router.push(`/instance/${metaId}`)
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

function openCheckIn(co) {
  // Single return mental model: route into scan/condition return flow.
  const tag = co.item_metadata?.assetTag
    || co.item_metadata?.asset_tag
    || co.item_metadata?.serial_number
    || co.item_metadata?.id
  const query = { checkout: co.id }
  if (tag) query.scan = String(tag)
  router.push({ path: '/return', query })
}

async function doCheckIn() {
  // Kept for BaseConfirmModal compatibility; primary path uses openCheckIn → /return.
  if (!checkInTarget.value) return
  openCheckIn(checkInTarget.value)
  checkInTarget.value = null
}

async function load() {
  loading.value   = true
  loadError.value = null
  try {
    // Support ?projectId= query param (e.g. navigated from ProjectView)
    const params = {}
    if (route.query.projectId) params.project = route.query.projectId
    const res        = await getAllCheckouts(params)
    const results    = res.results ?? res ?? []
    checkouts.value  = results
    totalCount.value = res.count ?? results.length
    overdueCount.value = res.overdue_count ?? 0
    // Pre-set project filter from URL if a specific project name was passed
    if (route.query.projectName) projectFilter.value = route.query.projectName
  } catch {
    loadError.value = t('checkoutsOverview.loadFailed')
  } finally {
    loading.value = false
  }
}

onMounted(load)
</script>


